import { _ } from "../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import accountRowVisibilityFactory from "../../../models/reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import FormBuilder from "../forms/FormBuilder.js";
import ReportDisplaySettings from "../../../models/reports/reports/ReportDisplaySettings.js";

const formatterSpecs = ReportDisplaySettings.allFormatterSpecs();

/**
 * Form to change the display settings of Forecast report.
 *
 * Technical point:
 *   Even thought the current implementation mutates the reportDisplaySettings provided as argument to this class,
 *   we should not rely on this mechanic, but use the instance provided by the `onChange` callback instead.
 *
 * @param {ReportDisplaySettings} [reportDisplaySettings] - Initial values for the ReportDisplaySettings that will be
 *   manipulated by the form
 * @param {function(ReportDisplaySettings): void} [onChange] - Callback triggered when one of the setting changes.
 *   The function is invoked with an instance of ReportDisplaySettings
 * @param {boolean} [showCreditPositiveSwitch] - Allows the hiding or rendering of the credit positive switch
 */
export default class ReportDisplaySettingsForm extends Widget {
	constructor({
		reportDisplaySettings = new ReportDisplaySettings(),
		showCreditPositiveSwitch = true,
		onChange = () => {},
	}) {
		super(...arguments);

		this._reportDisplaySettings = reportDisplaySettings;
		this._showCreditPositiveSwitch = showCreditPositiveSwitch;

		this._onChange = onChange;

		this._makeForm();
	}

	renderContentOn(html) {
		html.div({ class: "ReportDisplaySettingsForm" }, this._form);
	}

	_makeForm() {
		this._form = new FormBuilder({
			model: this.state,
			saveAction: () => this._onChange(this._reportDisplaySettings),
			showCancel: false,
			showActions: false,
		});

		this._addAccountVisibilityDropdown();
		this._addFormatterDropdown();
		this._addShowSummaryRowsSwitch();
		this._addCreditPositiveSwitch();
		this._addDefaultExpansionLevelInput();
	}

	_addAccountVisibilityDropdown() {
		this._form.newDropDownField({
			label: _("Account Visibility"),
			name: "account-visibility",
			accessor: () => this._reportDisplaySettings.getAccountVisibility(),
			mutator: (value) =>
				this._reportDisplaySettings.setAccountVisibility(value),
			onChange: () => this._form.submit(),
			items: accountRowVisibilityFactory
				.allUserVisibleAccountVisibilityClasses()
				.map((klass) => ({
					value: klass.instance(),
					label: klass.getLabel(),
					selected:
						this._reportDisplaySettings.getAccountVisibility() ===
						klass.instance(),
				})),
		});
	}

	_addFormatterDropdown() {
		this._form.newDropDownField({
			label: _("Format"),
			name: "format",
			accessor: () => this._reportDisplaySettings.getFormatter(),
			mutator: (value) => this._reportDisplaySettings.setFormatter(value),
			onChange: () => this._form.submit(),
			items: formatterSpecs.map(({ formatter, label }) => ({
				value: formatter,
				label,
				selected:
					this._reportDisplaySettings.getFormatter() === formatter,
			})),
		});
	}

	_addShowSummaryRowsSwitch() {
		this._addSwitch({
			name: "showSummaryRows",
			accessor: () => this._reportDisplaySettings.getShowSummaryRows(),
			mutator: (value) =>
				this._reportDisplaySettings.setShowSummaryRows(value),
			label: _("Shows summary rows"),
		});
	}

	_addCreditPositiveSwitch() {
		if (!this._showCreditPositiveSwitch) {
			return;
		}

		this._addSwitch({
			name: "debitPositive",
			accessor: () => this._reportDisplaySettings.getDebitPositive(),
			mutator: (value) =>
				this._reportDisplaySettings.setDebitPositive(value),
			label: _("Credit positive"),
		});
	}

	_addDefaultExpansionLevelInput() {
		const getLevel = () =>
			this._reportDisplaySettings.getDefaultExpansionLevel();

		this._form.newDropDownField({
			label: _("Default expansion level"),
			dataTestName: "default-expansion-level",
			accessor: getLevel,
			mutator: (value) =>
				this._reportDisplaySettings.setDefaultExpansionLevel(+value),
			onChange: () => this._form.submit(),
			items: this._makeLevelElements({
				defaultExpansionLevel: getLevel(),
			}),
		});
	}

	_addSwitch({ accessor, mutator, label, name }) {
		const switchWidget = this._form.switchButton({
			accessor,
			mutator,
			label,
			name,
		});

		switchWidget.stateChanged.register(() => this._form.submit());
	}

	_makeLevelElements({ defaultExpansionLevel = 2 }) {
		return [
			{
				value: 1,
				label: _("Level 1"),
				selected: defaultExpansionLevel === 1,
			},
			{
				value: 2,
				label: _("Level 2"),
				selected: defaultExpansionLevel === 2,
			},
			{
				value: 3,
				label: _("Level 3"),
				selected: defaultExpansionLevel === 3,
			},
			{
				value: 4,
				label: _("Level 4"),
				selected: defaultExpansionLevel === 4,
			},
			{
				value: Infinity,
				label: _("Expand all"),
				selected: defaultExpansionLevel === Infinity,
			},
		];
	}
}
